import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Menu, FileText, Settings, HelpCircle, KeyRound, LogOut, Stethoscope, DollarSign } from 'lucide-react';
import { roleLabel } from '../admin/adminShared';
import PushToggle from '../../components/PushToggle';

const authCfg = { withCredentials: true };
const isOfficeUser = (username, role, hospitalName) => username === 'superadmin' || (hospitalName === 'System Administration' && ['Admin', 'ViewOnly'].includes(role));

export default function DashboardHeader({
  role, hospitalName, username, isSuperAdmin, canViewAllData,
  maintenanceMode, onToggleMaintenance, workflowEnabled, pendingApprovals = 0,
  hospitalStats, todayStats,
  onOpenMonthlySummary, onOpenDailySummary, onChangePassword, onLogout,
}) {
  const navigate = useNavigate();
  const showWorkflow = role !== 'DataEntry' && (canViewAllData || workflowEnabled);

  const statsValues = Object.values(hospitalStats || {});
  const totalAmount = statsValues.reduce((sum, h) => (h && typeof h.total_pending === 'number' ? sum + h.total_pending + (h.total_paid || 0) : sum), 0);
  const totalCount = statsValues.reduce((sum, h) => (h && typeof h.pending_count === 'number' ? sum + h.pending_count + (h.paid_count || 0) : sum), 0);
  const avgClaim = totalCount > 0 ? totalAmount / totalCount : 0;

  return (
    <div className="bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0 flex-1 basis-full sm:basis-auto">
            <h1 className="text-lg sm:text-2xl font-bold text-gray-800 truncate">
              {role === 'Reception' ? 'Patient Search' : role === 'Finance' ? 'Claims Dashboard' : 'Hospital Dashboard'}
            </h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1 truncate">{hospitalName} • {username} • {roleLabel(role, localStorage.getItem('specialty'))}</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 sm:ml-2 w-full sm:w-auto justify-between sm:justify-end flex-wrap">
            {username === 'superadmin' && (
              <div className="flex items-center gap-2">
                <button
                  onClick={onToggleMaintenance}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${maintenanceMode ? 'bg-yellow-500' : 'bg-gray-300'}`}
                  data-testid="maintenance-mode-toggle"
                  title={`${maintenanceMode ? 'Maintenance ON' : 'Maintenance OFF'} | Auto: ON@11pm, OFF@8am`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-green-500 transition-transform ${maintenanceMode ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className="text-xs text-gray-400 hidden lg:inline" title="ON at 11pm, OFF at 8am CAT - Manual override resets at next boundary">
                  (11pm/8am)
                </span>
              </div>
            )}
            {isOfficeUser(username, role, hospitalName) && <PushToggle authCfg={authCfg} />}
            {showWorkflow && (
              <Button
                onClick={() => navigate(pendingApprovals > 0 ? '/workflow?tab=approvals' : '/workflow')}
                className="bg-teal-600 hover:bg-teal-700 text-white flex items-center gap-1 sm:gap-2 px-2 sm:px-3"
                data-testid="workflow-header-button"
                title={pendingApprovals > 0 ? `${pendingApprovals} approval${pendingApprovals > 1 ? 's' : ''} waiting (tests, prescriptions, pre-auth, admissions, referral letters)` : undefined}
              >
                <Stethoscope className="w-4 h-4" />
                <span className="hidden sm:inline">Patient Workflow</span>
                <span className="sm:hidden">Workflow</span>
                {pendingApprovals > 0 && (
                  <span className="ml-0.5 min-w-[1.25rem] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold animate-pulse" data-testid="pending-approvals-badge">
                    {pendingApprovals}
                  </span>
                )}
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="flex items-center gap-1 sm:gap-2 border-green-300 text-green-700 hover:bg-green-50 px-2 sm:px-3">
                  <Menu className="w-4 h-4" />
                  <span className="hidden sm:inline">Menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{hospitalName}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {role !== 'Reception' && role !== 'Finance' && role !== 'DataEntry' && (
                  <DropdownMenuItem onClick={onOpenMonthlySummary} className="cursor-pointer">
                    <FileText className="w-4 h-4 mr-2" />
                    Monthly Summary
                  </DropdownMenuItem>
                )}
                {role !== 'Reception' && role !== 'Finance' && role !== 'DataEntry' && (
                  <DropdownMenuItem onClick={onOpenDailySummary} className="cursor-pointer">
                    <FileText className="w-4 h-4 mr-2" />
                    Daily Summary
                  </DropdownMenuItem>
                )}
                {(isSuperAdmin || role === 'DataEntry') && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />
                    {role === 'DataEntry' ? 'Bulk Upload Panel' : 'Admin Panel'}
                  </DropdownMenuItem>
                )}
                {username === 'superadmin' && (
                  <DropdownMenuItem onClick={() => navigate('/reports')} className="cursor-pointer" data-testid="menu-reports-dashboard">
                    <FileText className="w-4 h-4 mr-2" />
                    Reports Dashboard
                  </DropdownMenuItem>
                )}
                {showWorkflow && (
                  <DropdownMenuItem onClick={() => navigate(pendingApprovals > 0 ? '/workflow?tab=approvals' : '/workflow')} className="cursor-pointer" data-testid="workflow-menu-item">
                    <Stethoscope className="w-4 h-4 mr-2" />
                    Patient Workflow
                    {pendingApprovals > 0 && <span className="ml-auto pl-3 text-xs font-bold text-red-600" data-testid="pending-approvals-menu-badge">{pendingApprovals} approval{pendingApprovals > 1 ? 's' : ''}</span>}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate('/help')} className="cursor-pointer">
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Help
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onChangePassword} className="cursor-pointer" data-testid="change-password-menu-item">
                  <KeyRound className="w-4 h-4 mr-2" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} className="cursor-pointer text-red-600 focus:text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {canViewAllData && (
          <div className="mt-3 grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-3 p-2 sm:px-3 sm:py-2 bg-red-50 border border-red-200 rounded-lg">
            <div className="text-center sm:text-left sm:flex sm:items-center sm:gap-2">
              <DollarSign className="w-4 h-4 text-red-600 hidden sm:block" />
              <div>
                <span className="text-xs text-red-600 font-medium block sm:inline">This Month</span>
                <span className="text-sm font-bold text-red-700 block sm:inline sm:ml-1">
                  ${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
                <span className="text-xs text-red-500 block sm:inline sm:ml-1">({totalCount})</span>
              </div>
            </div>
            <div className="text-center sm:text-left sm:border-l sm:border-red-300 sm:pl-3 sm:flex sm:items-center sm:gap-2">
              <div>
                <span className="text-xs text-blue-600 font-medium block sm:inline">Today</span>
                <span className="text-sm font-bold text-blue-700 block sm:inline sm:ml-1">
                  ${todayStats.total.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
                <span className="text-xs text-blue-500 block sm:inline sm:ml-1">({todayStats.count})</span>
              </div>
            </div>
            <div className="text-center sm:text-left sm:border-l sm:border-red-300 sm:pl-3 sm:flex sm:items-center sm:gap-2">
              <div>
                <span className="text-xs text-red-600 font-medium block sm:inline">Avg</span>
                <span className="text-sm font-bold text-red-700 block sm:inline sm:ml-1">
                  ${avgClaim.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
